'use client';

import React from 'react';
import Link from 'next/link';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export default function VirtualRenovationService() {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);

  const internalRenovations = [
    { description: 'Replace Flooring (change the texture of the existing flooring)', price: 'US$24.00', icon: '🏠' },
    { description: 'Replace Walls (change the texture of the original walls)', price: 'US$16.00', icon: '🧱' },
    { description: 'Replace Ceiling (change the texture of the existing ceiling)', price: 'US$16.00', icon: '⬆️' },
    { description: 'Item Removal & Virtual Cleaning (remove existing items that do not belong in the renovation)', price: 'US$8.00', icon: '🧹' },
    { description: 'Add/Remove Walls (add or remove a wall that divides a room)', price: 'US$40.00', icon: '🔨' },
    { description: 'Add or Complete Kitchen Fit Out (includes essential fixtures and structures e.g. Cabinets, Bench tops, splashback, etc. as well as standard accessories)', price: 'US$64.00', icon: '🍳' },
    { description: 'Add or Complete Bathroom Fit Out (includes essential fixtures and structures e.g. Cabinets, Bath, Shower, Mirror, Bench tops etc. as well as standard accessories)', price: 'US$64.00', icon: '🚿' },
    { description: 'Add Fixtures (add light fittings, door, wardrobes, windows, staircase or fireplace)', price: 'US$24.00', icon: '💡' },
    { description: 'Virtual Staging: Residential (includes standard furniture items e.g. Dining sets, Lounges, Beds, etc. as well as basic essential fixtures like wall mount TV, rugs, etc.)', price: 'US$24.00', icon: '🛋️' },
    { description: 'Virtual Staging: Commercial office (includes commercial furniture items e.g. Office desks, office chairs & office accessories)', price: 'US$24.00', icon: '💼' },
    { description: 'Virtual Staging: Commercial non-office (includes Commercial non-office furniture for cafes, bookstores, hair salon, fashion retail etc.)', price: 'US$24.00', icon: '☕' },
    { description: 'Color Change of Walls, Ceiling and/or Doors (non-texture surface change)', price: 'US$2.40', icon: '🎨' },
  ];

  const externalRenovations = [
    { description: 'Add Pool Only (keep existing surrounds)', price: 'US$40.00', icon: '🏊' },
    { description: 'Add Pool Surrounds (includes pool furniture, safety fencing, water feature, paving, concrete or decking connected to the pool itself. Does not include landscaping)', price: 'US$32.00', icon: '🪑' },
    { description: "Remove Pool Cover (Digitally remove a pool cover to highlight the pool's true beauty and elevate its overall appeal.)", price: 'US$40.00', icon: '🌊' },
    { description: 'Add Leaves (fill branches of bare trees with leaves)', price: 'US$24.00', icon: '🍃' },
    { description: 'Add Basic Landscaping (includes adding small pot plants, or adding leaves to existing trees)', price: 'US$24.00', icon: '🌿' },
    { description: 'Add Full Landscaping (includes lawn/grass, plants, garden beds, hedges, trees, driveways)', price: 'US$64.00', icon: '🌳' },
    { description: 'Season Change (includes a seasonal change with Full Landscaping of lawn/grass, plants, garden beds, hedges, trees, and driveways)', price: 'US$64.00', icon: '🍂' },
    { description: 'Virtual Staging Residential or Commercial (includes standard outdoor furniture items e.g. Outdoor dining sets, lounges, day beds, etc.)', price: 'US$24.00', icon: '🪴' },
    { description: 'Item Removal & Virtual Cleaning (remove existing items that do not belong in the renovation)', price: 'US$8.00', icon: '🧹' },
    { description: 'Add Fixtures (add a new exterior door, garage, windows, shutters or lights.)', price: 'US$24.00', icon: '🚪' },
    { description: 'Replace Driveway (change the texture of existing driveway)', price: 'US$24.00', icon: '🛣️' },
    { description: 'Replace Roof (change the texture/color of existing roof)', price: 'US$24.00', icon: '🏠' },
    { description: 'Replace external walls (renovate the texture/finish of existing exterior wall/building)', price: 'US$48.00', icon: '🧱' },
    { description: 'Add or complete either a Patio, Balcony, Pergola, Veranda and/or Decking', price: 'US$48.00', icon: '🏡' },
    { description: 'Color Change Exterior Feature (change the color only (not the texture or material) of the house exterior features)', price: 'US$2.40', icon: '🎨' },
  ];

  const benefits = [
    { title: 'Take the Unknown Out of Renovations', description: 'Visualize what a design will look like before undertaking expensive renovations.', icon: '🔮' },
    { title: 'Portray the Finished Room', description: 'Help buyers see the true potential of a home before it is complete.', icon: '✨' },
    { title: 'Sell an Unfinished Property', description: 'Show buyers what the property will look like once it is fully complete.', icon: '🏗️' },
    { title: 'Increase Buyer Interest', description: "90% of buyers start looking online, where you only have a few moments to catch a buyer's interest.", icon: '📈' },
  ];

  const transformationExamples = [
    { title: 'Complete Transformations', price: 'US$184', services: ['Add/Remove Walls', 'Replace Flooring', 'Add Fixtures', 'Virtual Staging', 'Add or Complete Kitchen Fit Out', 'Remove Objects/Blemishes'], before: '/images/gallery/1vfbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/1vfaf.webp?auto=compress&cs=tinysrgb&w=600' },
    { title: 'Pool Installation', price: 'US$72', services: ['Add Pool Only', 'Add Pool Surrounds'], before: '/images/gallery/2vfbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/2vfaf.webp?auto=compress&cs=tinysrgb&w=600' },
    { title: 'Kitchen Fitout', price: 'US$136', services: ['Add or Complete Kitchen Fit Out', 'Replace Flooring', 'Add Fixtures', 'Virtual Staging'], before: '/images/gallery/3vfbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/3vfaf.webp?auto=compress&cs=tinysrgb&w=600' },
    { title: 'Small Renovations', price: 'US$24', services: ['Replace Flooring'], before: '/images/gallery/floorbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/flooraf.webp?auto=compress&cs=tinysrgb&w=600' },
    { title: 'Bathroom Overhaul', price: 'US$136', services: ['Replace Flooring', 'Replace Walls', 'Add Fixtures', 'Add or Complete Bathroom Fit Out', 'Remove Objects/Blemishes'], before: '/images/gallery/4vfbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/4vfaf.webp?auto=compress&cs=tinysrgb&w=600' },
    { title: 'Outdoor Extensions', price: 'US$72', services: ['Add or complete Patio, Balcony, Pergola, Veranda and/or Decking', 'Add Basic Landscaping'], before: '/images/gallery/5vfbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/5vfaf.webp?auto=compress&cs=tinysrgb&w=600' },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  const Badge = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center bg-white/[0.04] border border-white/[0.12] text-purple-200 px-6 py-2 rounded-full text-sm font-bold">
      {children}
    </span>
  );

  const RenovationTable = ({ title, items }: { title: string; items: { description: string; price: string; icon: string }[] }) => (
    <div>
      <h3 className="text-2xl lg:text-3xl font-bold text-white mb-6 text-center">{title}</h3>
      <div className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08]">
        <div className="grid grid-cols-[1fr_auto] gap-x-4 px-6 py-4 bg-gradient-to-r from-purple-600/30 to-blue-600/20 border-b border-white/[0.08] text-sm font-bold text-white">
          <span>Description</span>
          <span className="text-right">Price</span>
        </div>
        <div className="divide-y divide-white/[0.06]">
          {items.map((item, index) => (
            <div key={index} className="grid grid-cols-[1fr_auto] gap-x-4 px-6 py-4 hover:bg-white/[0.03] transition-colors items-start">
              <div className="flex items-start gap-3">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <span className="text-gray-300 text-sm leading-relaxed">{item.description}</span>
              </div>
              <span className="font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent text-lg whitespace-nowrap">{item.price}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17] border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
                <span className="text-sm">🏗️</span>
                <span>Turn an idea into reality from just US$24</span>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                  Virtual{' '}
                  <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                    renovation.
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Visualize what a design will look like before undertaking expensive renovations. Help buyers see
                  the true potential of a home and sell unfinished properties with confidence.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                    Get Started Now
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#pricing">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    View Pricing
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4">
                {[
                  { metric: '$24', label: 'Starting Price' },
                  { metric: '90%', label: 'Start Online' },
                  { metric: '60s', label: 'To Get Started' },
                ].map((s, i) => (
                  <div key={i} className="text-center bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-1">{s.metric}</div>
                    <div className="text-xs font-semibold text-white">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="relative h-[450px] lg:h-[550px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
                <ProtectedAppImage
                  src="/images/gallery/4vfaf.webp?auto=compress&cs=tinysrgb&w=1200"
                  alt="Virtual renovation transformation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-3 rounded-lg font-bold text-sm shadow-lg shadow-purple-500/30">
                  AFTER RENOVATION
                </div>
                <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-5 py-3 rounded-lg font-bold text-sm">
                  VISUALIZE BEFORE SPENDING
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY VIRTUAL RENOVATION ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Why virtual renovation?</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Transform unfinished or outdated properties into dream homes. Virtual renovation makes buyers see
              potential instead of problems.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-3xl mb-5 group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PRICING TABLES ===================== */}
      <section id="pricing" className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>HOW DOES PRICING WORK?</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Transparent pricing structure</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Virtual renovations vary in price depending on how simple or extensive your renovations are. Put
              yourself in the renovator&apos;s shoes and imagine what has to be completed — this is how our pricing works.
            </p>
          </div>

          <div className="space-y-12">
            <RenovationTable title="Internal Renovations" items={internalRenovations} />
            <RenovationTable title="External Renovations" items={externalRenovations} />
          </div>
        </div>
      </section>

      {/* ===================== TRANSFORMATION EXAMPLES ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">The virtual renovation process</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              See some amazing transformations below and the edits &amp; cost that coincide with the work.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {transformationExamples.map((example, index) => (
              <div key={index} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.before} alt={`${example.title} - Before`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded">BEFORE</div>
                  </div>
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.after} alt={`${example.title} - After`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30">AFTER</div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg text-white">{example.title}</h3>
                    <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{example.price}</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-300 mb-2">Services Included:</p>
                    {example.services.map((service, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-400">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GET STARTED ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Get started in under 60 seconds</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Ready to transform your property? Sign up now and start your virtual renovation project today.
            </p>
          </div>
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-2xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.5)] p-8 lg:p-12">
            <div className="absolute -inset-4 bg-purple-600/10 rounded-3xl blur-[80px] -z-10" />
            <div className="text-center mb-8">
              <span className="inline-block bg-purple-500/10 border border-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-bold">
                STEP 1 OF 3
              </span>
            </div>
            <p className="text-gray-300 text-center text-lg mb-6">
              Create your free account to access our virtual renovation services and start visualizing your
              property&apos;s potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                  Create Free Account
                </button>
              </Link>
              <Link href="/auth/signin">
                <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                  Sign In
                </button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 text-center mt-6">No credit card required • Start immediately • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* ===================== HELP SECTION ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Want help to get started?</h2>
          <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-3xl mx-auto">
            Above you&apos;ll find a full price list with a number of virtual renovation options. Add the options you
            need to build a total price per photo, with real examples to guide you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="#pricing">
              <button className="px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                View Full Price List
              </button>
            </Link>
            <Link href={signupUrl}>
              <button className="px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                Start Your Project
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Turn tired properties into{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">dream homes.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Professional virtual renovations that help buyers fall in love with potential. Visualize before you
              spend, sell faster, and increase buyer interest.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Renovating Now
                </button>
              </Link>
              <Link href="#pricing">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  View Pricing
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
